import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import { useConfirmGuard } from '../hooks/useConfirmGuard';

/**
 * LEADS1 - "Lead sending: on/off". Off = shadow mode: approvals are recorded as "would have sent", nothing reaches a
 * lead.
 *
 * ⛔ UNREADABLE IS NOT OFF. The server answers `readable: false` when it could not read the switch; it then sends
 *    nothing, but the switch itself may well be ON - so the line says UNKNOWN, never "OFF". A GET that never answered
 *    says less still: the page does not know what the server thinks. Both offer a look again.
 * ⛔ THE SAFE ACTION IS ALWAYS ONE TAP. "Turn off" is offered whenever the switch is not known to be off - unknown and
 *    unreachable included.
 * ⛔ ON TAKES A SECOND TAP A DOUBLE TAP CANNOT SUPPLY: the confirm is its own row, "Keep it off" first and holding
 *    focus, and the dangerous button ignores taps for CONFIRM_GUARD_MS (useConfirmGuard).
 * ⛔ A SWITCH READ ONCE GOES STALE: turned on from the desktop, a phone tab read at breakfast would still say OFF. It is
 *    read again whenever `refreshKey` changes (the page bumps it on every queue reload) and whenever the tab becomes
 *    visible.
 * ⭐ The line shows what the server says it STORED, never what was asked for - and says so when the two differ.
 */
type SwitchView =
  | { kind: 'loading' }
  | { kind: 'unreachable'; message: string }
  | { kind: 'known'; enabled: boolean; readable: boolean };

const TURN_ON_CONFIRM = 'Yes - really send approved messages to leads';

interface Props {
  /** Changes on every queue reload; each change is a fresh read. */
  refreshKey?: number;
  /** The clock the double-tap guard reads. Tests pass their own. */
  now?: () => number;
}

export default function LeadSendingToggle({ refreshKey = 0, now }: Props) {
  const [view, setView] = useState<SwitchView>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const confirm = useConfirmGuard(now);
  const { back } = confirm;
  const keepOff = useRef<HTMLButtonElement>(null);
  const turnOn = useRef<HTMLButtonElement>(null);
  /** Set by "Keep it off": when the confirm closes, focus goes back to Turn on rather than to the top of the page. */
  const focusTurnOn = useRef(false);
  /**
   * ⛔ AN ANSWER PAINTS UNLESS A NEWER ONE ALREADY HAS. Each read and each flip takes a ticket in the order asked;
   *    `painted` is the newest ticket on screen. An older answer arriving late is dropped - but a newer read that
   *    HANGS does not stop an older good answer from showing (the rule is "newer painted", not "newer asked").
   *    A flip marks its own ticket painted, so every read asked before it is stale. And no read starts DURING a flip:
   *    it could reach the server before the write lands and paint the OLD value over the flip's answer.
   */
  const ticket = useRef(0);
  const painted = useRef(0);
  const flipping = useRef(false);
  const mayPaint = (mine: number): boolean => {
    if (mine <= painted.current) return false;
    painted.current = mine;
    return true;
  };

  const read = useCallback(async () => {
    if (flipping.current) return;
    const mine = ++ticket.current;
    try {
      const r = await api.getLeadSending();
      if (!mayPaint(mine)) return;
      // Literal true only, on both: a missing `readable` is not proof the server could read it.
      const enabled = r.data.enabled === true;
      const readable = r.data.readable === true;
      setView({ kind: 'known', enabled, readable });
      // The "turn on?" question only makes sense while the switch is known to be off.
      if (enabled || !readable) back();
    } catch (e) {
      if (!mayPaint(mine)) return;
      setView({ kind: 'unreachable', message: e instanceof Error ? e.message : 'No answer from the server' });
      back();
    }
  }, [back]);

  useEffect(() => {
    void read();
  }, [read, refreshKey]);

  useEffect(() => {
    const onShow = () => {
      if (document.visibilityState === 'visible') void read();
    };
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, [read]);

  useEffect(() => {
    if (confirm.open) keepOff.current?.focus();
    else if (focusTurnOn.current) turnOn.current?.focus();
    focusTurnOn.current = false;
  }, [confirm.open]);

  const set = async (enabled: boolean) => {
    flipping.current = true;
    const mine = ++ticket.current;
    setBusy(true);
    setNote(null);
    back();
    try {
      const r = await api.setLeadSending(enabled);
      painted.current = Math.max(painted.current, mine); // every read asked before the flip is now stale
      const stored = r.data.enabled === true;
      setView({ kind: 'known', enabled: stored, readable: true });
      if (stored !== enabled) setNote(`The server kept lead sending ${stored ? 'ON' : 'OFF'}`);
    } catch (e) {
      setNote(`Could not change lead sending - ${e instanceof Error ? e.message : 'try again'}`);
      // The flip may or may not have landed; read what the server holds rather than guess.
      flipping.current = false;
      await read();
    } finally {
      flipping.current = false;
      setBusy(false);
    }
  };

  if (view.kind === 'loading') return null;

  const button = 'rounded border border-gray-300 px-3 py-1 disabled:opacity-50';
  const turnOff = (
    <button type="button" disabled={busy} className={button} onClick={() => void set(false)}>
      Turn off
    </button>
  );
  let line: string;
  let detail: string | null = null;
  let actions: ReactNode;
  if (view.kind === 'unreachable' || !view.readable) {
    line =
      view.kind === 'unreachable'
        ? 'Lead sending: could not check it'
        : 'Lead sending: UNKNOWN - treated as OFF (nothing is sent)';
    if (view.kind === 'unreachable') detail = view.message;
    actions = (
      <>
        <button type="button" disabled={busy} className={button} onClick={() => void read()}>
          Try again
        </button>
        {turnOff}
      </>
    );
  } else if (view.enabled) {
    line = 'Lead sending: ON - approved messages go to leads';
    actions = turnOff;
  } else {
    line = 'Lead sending: OFF (shadow - nothing is sent)';
    actions = confirm.open ? null : (
      <button ref={turnOn} type="button" disabled={busy} className={button} onClick={confirm.ask}>
        Turn on
      </button>
    );
  }

  return (
    <div className="mb-4 min-w-0 rounded border border-gray-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="break-words font-medium">{line}</span>
        {actions}
      </div>
      {detail && <p className="mt-1 break-words text-xs text-gray-600">{detail}</p>}
      {view.kind === 'known' && view.readable && !view.enabled && confirm.open && (
        <div role="group" aria-label="Confirm turning lead sending on" className="mt-2 rounded border border-red-300 bg-red-50 p-2">
          <p className="text-xs text-red-800">Messages you approve from now on will go to real leads.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              ref={keepOff}
              type="button"
              disabled={busy}
              className={`${button} bg-white`}
              onClick={() => {
                focusTurnOn.current = true;
                back();
              }}
            >
              Keep it off
            </button>
            <button
              type="button"
              disabled={busy}
              className="rounded bg-red-600 px-3 py-1 text-white disabled:opacity-50"
              onClick={() => {
                if (confirm.armed()) void set(true);
              }}
            >
              {TURN_ON_CONFIRM}
            </button>
          </div>
        </div>
      )}
      {note && <p className="mt-2 break-words text-xs text-red-800">{note}</p>}
    </div>
  );
}
